/**
 * DTO → view-model mappers for the `Family`, `FamilySummary`, and
 * `FamilyMemberRecord` resources.
 *
 * Same stability rules as `src/api/mappers/_shared.ts`:
 *   - Every snake_case → camelCase rename is explicit
 *   - Optional DTO fields become `null` (never `undefined`) on the view model
 *   - Unknown enum values collapse to a safe fallback so the UI never has to
 *     reason about server-side enum drift.
 */

import { narrowEnum, nullableString } from '@/api/mappers/_shared';
import type {
  CascadeContactStatus,
  CascadeRole,
  CascadeStatus,
  FamilyMemberViewModel,
  FamilySummaryViewModel,
  FamilyViewModel,
  PhysicalChartViewModel,
} from '@/models/family';

import type { FamilyDto, FamilyExtensionsDto, FamilyMemberRecordDto, FamilySummaryDto } from './api';

const KNOWN_CASCADE_STATUS: ReadonlySet<CascadeStatus> = new Set<CascadeStatus>([
  'NOT_STARTED',
  'IN_PROGRESS',
  'COMPLETE',
  'CLOSED',
]);

const KNOWN_CASCADE_ROLE: ReadonlySet<CascadeRole> = new Set<CascadeRole>([
  'PROBAND',
  'FAMILY_MEMBER',
]);

const KNOWN_CONTACT_STATUS: ReadonlySet<CascadeContactStatus> =
  new Set<CascadeContactStatus>(['NOT_CONTACTED', 'CONTACTED', 'DECLINED', 'TESTED']);

export function mapFamilySummary(dto: FamilySummaryDto): FamilySummaryViewModel | null {
  const guid = nullableString(dto.guid);
  if (guid === null) return null;
  // `proband_name` / `proband_is_patient` ship in PTV2-1148 but the checked-in OpenAPI snapshot
  // hasn't been regenerated yet. Read them permissively until `pnpm generate:api` catches up.
  const extras = dto as FamilySummaryDto & {
    readonly proband_name?: string | null;
    readonly proband_is_patient?: boolean | null;
  };
  return {
    guid,
    name: nullableString(dto.name) ?? '',
    probandGuid: nullableString(dto.proband_guid),
    probandName: nullableString(extras.proband_name),
    probandIsPatient: extras.proband_is_patient === true,
    cascadeStatus: narrowEnum<CascadeStatus>(dto.cascade_status, KNOWN_CASCADE_STATUS, 'NOT_STARTED'),
    memberCount: typeof dto.member_count === 'number' ? dto.member_count : 0,
    updatedAt: nullableString(dto.updated_at),
  };
}

export function mapFamilySummaries(
  dtos: readonly FamilySummaryDto[] | undefined,
): readonly FamilySummaryViewModel[] {
  if (dtos === undefined) return [];
  const out: FamilySummaryViewModel[] = [];
  for (const dto of dtos) {
    const mapped = mapFamilySummary(dto);
    if (mapped !== null) out.push(mapped);
  }
  return out;
}

function mapMember(
  dto: FamilyMemberRecordDto,
  familyGuid: string,
): FamilyMemberViewModel | null {
  const id = nullableString(dto.id);
  if (id === null) return null;
  // `sex`, `deceased`, `notes` were added to FamilyMemberRecordDTO in
  // PTV2-1028 and `is_patient` / `connected_in_pedigree` in PTV2-1148, but
  // the checked-in OpenAPI snapshot hasn't been regenerated yet. Read them
  // permissively until `pnpm generate:api` catches up.
  const extras = dto as FamilyMemberRecordDto & {
    readonly sex?: string | null;
    readonly deceased?: boolean | null;
    readonly notes?: string | null;
    readonly is_patient?: boolean | null;
    readonly connected_in_pedigree?: boolean | null;
  };
  return {
    id,
    familyGuid,
    personGuid: nullableString(dto.person_guid),
    isPatient: extras.is_patient === true,
    connectedInPedigree: extras.connected_in_pedigree === true,
    firstName: nullableString(dto.first_name),
    lastName: nullableString(dto.last_name),
    dateOfBirth: nullableString(dto.date_of_birth),
    cascadeRole: narrowEnum<CascadeRole>(dto.cascade_role, KNOWN_CASCADE_ROLE, 'FAMILY_MEMBER'),
    contactStatus: narrowEnum<CascadeContactStatus>(
      dto.contact_status,
      KNOWN_CONTACT_STATUS,
      'NOT_CONTACTED',
    ),
    sex: nullableString(extras.sex),
    deceased: typeof extras.deceased === 'boolean' ? extras.deceased : null,
    notes: nullableString(extras.notes),
  };
}

export function mapFamilyMember(
  dto: FamilyMemberRecordDto,
  familyGuid: string,
): FamilyMemberViewModel | null {
  return mapMember(dto, familyGuid);
}

function mapPhysicalChart(
  value: FamilyExtensionsDto['physical_chart'],
): PhysicalChartViewModel | null {
  if (value === null || value === undefined) return null;
  const id = nullableString(value.id);
  const date = nullableString(value.date);
  const location = nullableString(value.location);
  const staffMemberUserGuid = nullableString(value.staff_member_user_guid);
  const notes = nullableString(value.notes);
  if (date === null && location === null && staffMemberUserGuid === null && notes === null) {
    return null;
  }
  return { id, date, location, staffMemberUserGuid, notes };
}

export function mapFamilyMembers(
  dtos: readonly FamilyMemberRecordDto[] | undefined,
  familyGuid: string,
): readonly FamilyMemberViewModel[] {
  if (dtos === undefined) return [];
  const out: FamilyMemberViewModel[] = [];
  for (const raw of dtos) {
    const mapped = mapMember(raw, familyGuid);
    if (mapped !== null) out.push(mapped);
  }
  return out;
}

export function mapFamily(
  dto: FamilyDto,
  membersOverride?: readonly FamilyMemberRecordDto[],
): FamilyViewModel {
  const memberDtos = membersOverride ?? dto.members ?? [];
  const members = mapFamilyMembers(memberDtos, dto.guid);
  // `custom_label` / `physical_chart` ship in PTV2-1029 / PTV2-1031 but the
  // checked-in OpenAPI schema hasn't been regenerated yet. Read them
  // permissively until `pnpm generate:api` catches up.
  const extensions = dto as FamilyDto & FamilyExtensionsDto;
  // Spec truth (api-docs 2026-07): `FamilyDTO` has no `proband_guid` — only
  // `FamilySummaryDTO` carries it. The checked-in `schema.ts` still lists
  // the field, so we read it defensively when present and otherwise derive
  // from the PROBAND member row so the proband panel + identity band
  // render correctly on `GET /families/{guid}` responses.
  const wireProbandGuid = nullableString(dto.proband_guid);
  const derivedProbandGuid =
    wireProbandGuid ??
    members.find((m) => m.cascadeRole === 'PROBAND' && m.personGuid !== null)?.personGuid ??
    null;
  return {
    guid: dto.guid,
    name: nullableString(dto.name) ?? '',
    probandGuid: derivedProbandGuid,
    cascadeStatus: narrowEnum<CascadeStatus>(dto.cascade_status, KNOWN_CASCADE_STATUS, 'NOT_STARTED'),
    notes: nullableString(dto.notes),
    customLabel: nullableString(extensions.custom_label),
    physicalChart: mapPhysicalChart(extensions.physical_chart),
    members,
    createdAt: nullableString(dto.created_at),
    updatedAt: nullableString(dto.updated_at),
  };
}

export function getFamilyMemberFullName(
  m: Pick<FamilyMemberViewModel, 'firstName' | 'lastName'>,
): string | null {
  const parts = [m.firstName, m.lastName].filter((p): p is string => p !== null && p.length > 0);
  return parts.length === 0 ? null : parts.join(' ');
}
